import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useTeamProjects, useCreateTeamProject, useArchiveTeamProject } from '@/hooks/useTeamProjects';
import { TeamProjectCard } from '@/components/team/TeamProjectCard';
import { TeamProjectForm } from '@/components/team/TeamProjectForm';
import { EmptyState } from '@/components/EmptyState';
import { TeamProject } from '@/types/TeamProject';

export default function TeamDashboard() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();

  const { data: projects = [], isLoading } = useTeamProjects();
  const createProject = useCreateTeamProject();
  const archiveProject = useArchiveTeamProject();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <div className="pt-16 p-6">
          <div className="max-w-7xl mx-auto">
            <p>Loading team projects...</p>
          </div>
        </div>
      </div>
    );
  }

  const handleCreateProject = (projectData: Omit<TeamProject, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'member_count' | 'user_role'>) => {
    createProject.mutate(projectData);
    setShowCreateForm(false);
  };

  const handleSelectProject = (project: TeamProject) => {
    navigate(`/team-projects/${project.id}`);
  };

  const handleArchiveProject = (projectId: string) => {
    if (confirm('Are you sure you want to archive this project?')) {
      archiveProject.mutate(projectId);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <div className="pt-16 p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Team Projects</h1>
              <p className="text-muted-foreground">
                Collaborate with your team on projects and track time together
              </p>
            </div>
            <Button 
              onClick={() => setShowCreateForm(true)} 
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              New Team Project
            </Button>
          </div>

          {/* Create Project Form */}
          {showCreateForm && (
            <TeamProjectForm
              onSubmit={handleCreateProject}
              onCancel={() => setShowCreateForm(false)}
              isSubmitting={createProject.isPending}
            />
          )}

          {/* Projects List */}
          {projects.length === 0 ? (
            <EmptyState
              title="No team projects yet"
              description="Create your first team project to start collaborating with others."
              actionLabel="Create Team Project"
              onAction={() => setShowCreateForm(true)}
            />
          ) : (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {projects.map((project) => (
                <TeamProjectCard
                  key={project.id}
                  project={project}
                  onSelect={handleSelectProject}
                  onArchive={handleArchiveProject}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}